import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Coins, Gauge, ShieldAlert, X } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetTitle,
  SheetClose,
} from "./ui/sheet";
import { Separator } from "./ui/separator";
import { BLOCKER_CATEGORIES } from "../constants/entitlementBlockers";

const CATEGORY_ICON_MAP = {
  [BLOCKER_CATEGORIES.FEATURE_LOCKED]: ShieldAlert,
  [BLOCKER_CATEGORIES.LIMIT_REACHED]: Gauge,
  [BLOCKER_CATEGORIES.QUOTA_EXHAUSTED]: AlertTriangle,
  [BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS]: Coins,
};

const CATEGORY_BADGE_MAP = {
  [BLOCKER_CATEGORIES.FEATURE_LOCKED]: "Feature Lock",
  [BLOCKER_CATEGORIES.LIMIT_REACHED]: "Limit Reached",
  [BLOCKER_CATEGORIES.QUOTA_EXHAUSTED]: "Quota Exhausted",
  [BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS]: "Credits Needed",
};

const formatUsageValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (value === Infinity) return "Unlimited";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return parsed.toLocaleString();
};

const UsageGrid = ({ usageMeta }) => {
  if (!usageMeta) return null;

  const rows = [
    { label: "Current Usage", value: usageMeta.currentUsage },
    { label: "Allowed Limit", value: usageMeta.allowedLimit },
    { label: "Remaining", value: usageMeta.remaining },
    { label: "Required Now", value: usageMeta.requiredAmount },
  ].filter((row) => row.value !== null && row.value !== undefined);

  if (rows.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-2 mt-3">
      {rows.map((row) => (
        <div
          key={row.label}
          className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {row.label}
          </p>
          <p className="text-sm font-bold text-foreground mt-1">
            {formatUsageValue(row.value)}
          </p>
        </div>
      ))}
    </div>
  );
};

export default function EntitlementBlockerModal({ open, model, onClose }) {
  const navigate = useNavigate();

  if (!open || !model) return null;

  const Icon = CATEGORY_ICON_MAP[model.blockerCategory] || ShieldAlert;
  const categoryBadge = CATEGORY_BADGE_MAP[model.blockerCategory] || "Blocked";

  const runCta = (cta) => {
    onClose();
    if (cta?.to) navigate(cta.to);
  };

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <SheetContent side="right" className="sm:max-w-lg rounded-2xl border border-border bg-card shadow-2xl p-0 overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-br from-primary/15 via-background to-background border-b border-border/60">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl border border-primary/20 bg-primary/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-lg font-black tracking-tight text-foreground">
                  {model.title}
                </SheetTitle>
                <div className="mt-1 flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="text-[9px] font-black uppercase tracking-wider"
                  >
                    {categoryBadge}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="text-[9px] font-black uppercase tracking-wider"
                  >
                    Plan: {model.planCode || "FREE"}
                  </Badge>
                </div>
              </div>
            </div>
            <SheetClose asChild>
              <button
                type="button"
                className="h-8 w-8 rounded-lg border border-border/60 bg-background/80 text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close blocker modal</span>
              </button>
            </SheetClose>
          </div>
        </div>

        <div className="px-6 pt-5 pb-0">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {model.description}
          </p>
        </div>

        <div className="px-6 pb-5">
          {model.blockedItemLabel && (
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 mt-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Blocked Item
              </p>
              <p className="text-sm font-semibold text-foreground mt-1">
                {model.blockedItemLabel}
              </p>
            </div>
          )}

          <UsageGrid usageMeta={model.usageMeta} />

          {model.footnote && (
            <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
              {model.footnote}
            </p>
          )}
        </div>

        <Separator className="bg-border/60" />

        <SheetFooter className="px-6 py-4 gap-2 sm:justify-end">
          {model.secondaryCta && (
            <SheetClose asChild>
              <Button
                variant="outline"
                className="rounded-xl h-10 font-semibold"
                onClick={() => runCta(model.secondaryCta)}
              >
                {model.secondaryCta.label}
              </Button>
            </SheetClose>
          )}
          {model.primaryCta && (
            <SheetClose asChild>
              <Button
                className="rounded-xl h-10 font-black tracking-wide"
                onClick={() => runCta(model.primaryCta)}
              >
                {model.primaryCta.label}
              </Button>
            </SheetClose>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
